import { ReactNode } from 'react';
import { DateValue, ZonedDateTime } from '@internationalized/date';
import { MdOutlineCalendarToday } from 'react-icons/md';
import { Calendar, Popover, PopoverContent, PopoverTrigger, TimeInput } from '@ds';
import { truncateToMinutes } from '@/utils/helpers';

const CALENDAR_ICON_SIZE = 18;
const CALENDAR_WIDTH = '16.25rem';

type DateTimeSettingsPopoverProps = {
  value: ZonedDateTime;
  timeInputLabel: string;
  minValue?: ZonedDateTime;
  extraContent?: ReactNode;
  onChange: (dateTime: DateValue) => void;
};

export const DateTimeSettingsPopover = ({
  value,
  timeInputLabel,
  minValue,
  extraContent,
  onChange,
}: DateTimeSettingsPopoverProps) => {
  const minDateTime = minValue && truncateToMinutes(minValue);

  const handleChange = (dateTime: ZonedDateTime | null) => {
    if (!dateTime) return;

    const truncatedDateTime = truncateToMinutes(dateTime);
    const isBeforeMin = minDateTime && truncatedDateTime.compare(minDateTime) < 0;

    onChange(isBeforeMin ? minDateTime : truncatedDateTime);
  };

  return (
    <Popover placement="top">
      <PopoverTrigger>
        <div className="text-base-10 w-4 h-4 flex justify-center items-center">
          <MdOutlineCalendarToday size={CALENDAR_ICON_SIZE} />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0 tmdx-calendar tmdx-calendar__popover bg-base-60 shadow-xl">
        <Calendar
          value={value}
          minValue={minDateTime}
          calendarWidth={CALENDAR_WIDTH}
          showHelper={false}
          onChange={handleChange}
          classNames={{
            content: 'tmdx-calendar__content',
          }}
          bottomContent={
            <div className="flex flex-col bg-base-60">
              <div className="flex justify-between items-center gap-2 px-4 pt-1 pb-3">
                <span className="text-l-m">Time</span>
                <TimeInput
                  size="sm"
                  aria-label={timeInputLabel}
                  hourCycle={24}
                  value={value}
                  classNames={{
                    inputWrapper: 'bg-base-90',
                  }}
                  onChange={handleChange}
                />
              </div>
              {extraContent}
            </div>
          }
        />
      </PopoverContent>
    </Popover>
  );
};
